import {
  AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges,
  ViewChild
} from '@angular/core';
import {Chart} from 'chart.js/auto';
import {ChartConfiguration, ChartDataset, TooltipItem} from 'chart.js';
import {EventiCounter} from '../../../_models/EventiCounter';
import {fontTick, isMobileGrafico, legendaResponsive} from '../chart-defaults';

type TipoBarre = 'bar' | 'line';

@Component({
  selector: 'app-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
  standalone: false
})
export class BarChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() bars: EventiCounter[] = [];
  @Input() chartName: string = '';
  @Input() colors?: string[];
  @Input() titolo = 'Eventi per giorno - ultimo mese';
  /** Feature 161: «Eventi» come linea su un asse destro, così le altre serie restano leggibili. */
  @Input() eventiComeLinea = false;
  /** Feature 161: solo gli ultimi N giorni. */
  @Input() giorniMostrati?: number;
  /** Feature 161: riga di link ai giorni sotto il grafico, per raggiungere il clic da tastiera. */
  @Input() linkGiorni = false;
  @Output() giornoSelezionato = new EventEmitter<EventiCounter>();
  @ViewChild('barCanvas') canvasRef?: ElementRef<HTMLCanvasElement>;
  public chart: Chart<TipoBarre, number[], string>;
  private viewInitialized = false;
  private readonly zone = inject(NgZone);

  get barreMostrate(): EventiCounter[] {
    const bars = this.bars ?? [];
    return this.giorniMostrati && this.giorniMostrati > 0 ? bars.slice(-this.giorniMostrati) : bars;
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.viewInitialized && (changes['bars'] || changes['giorniMostrati'] || changes['eventiComeLinea'])) {
      setTimeout(() => this.createChart(), 0);
    }
  }

  ngOnDestroy() {
    this.distruggi();
  }

  etichettaGiorno(bar: EventiCounter): string {
    return new Date(bar.day).toLocaleDateString();
  }

  createChart() {
    if (!this.canvasRef) {
      return;
    }
    this.distruggi();
    const bars = this.barreMostrate;
    if (bars.length === 0) {
      return;
    }
    // Fuori dalla zona Angular (come in #3717): ridimensionamenti e animazioni di chart.js non rilanciano la change
    // detection dell'intera pagina; il clic su un giorno rientra in zona per navigare.
    const canvas = this.canvasRef.nativeElement;
    const configurazione = this.configurazione(bars, isMobileGrafico());
    this.chart = this.zone.runOutsideAngular(() => new Chart(canvas, configurazione));
  }

  private distruggi() {
    const chart = this.chart;
    if (chart) this.zone.runOutsideAngular(() => chart.destroy());
    this.chart = null;
  }

  configurazione(bars: EventiCounter[], isMobile: boolean): ChartConfiguration<TipoBarre, number[], string> {
    const defaultColors = ['blue', 'violet', 'green', 'gray'];
    const c = (i: number) => this.colors?.[i] ?? defaultColors[i];
    const barra = (label: string, data: number[], i: number): ChartDataset<TipoBarre, number[]> => ({
      label, data, backgroundColor: c(i), borderRadius: Number.MAX_VALUE, borderSkipped: false
    });
    const eventi = barra('Eventi', bars.map(b => b.count), 0);
    return {
      type: 'bar',
      data: {
        labels: bars.map(b => this.etichettaGiorno(b)),
        datasets: [
          this.eventiComeLinea ? this.lineaEventi(eventi, c(0)) : eventi,
          barra('Ordini lanciati', bars.map(b => b.countStart), 1),
          barra('Ordini terminati', bars.map(b => b.countDone), 2),
          barra('Ordini inviati', bars.map(b => b.sendDone), 3)
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (_e, elementi) => {
          if (elementi.length > 0) this.zone.run(() => this.giornoSelezionato.emit(bars[elementi[0].index]));
        },
        plugins: {
          legend: legendaResponsive(isMobile, [14, 20]),
          ...(this.eventiComeLinea ? {tooltip: {callbacks: {label: etichettaTooltip}}} : {})
        },
        scales: this.scale(isMobile)
      }
    };
  }

  private lineaEventi(barra: ChartDataset<TipoBarre, number[]>, colore: string): ChartDataset<TipoBarre, number[]> {
    return {
      type: 'line', label: barra.label, data: barra.data, borderColor: colore, backgroundColor: colore,
      yAxisID: 'y1', tension: 0.25, pointRadius: 2, order: 0
    };
  }

  private scale(isMobile: boolean) {
    const scale: ChartConfiguration<TipoBarre>['options']['scales'] = {
      x: {
        ticks: {
          autoSkip: true,
          maxTicksLimit: isMobile ? 6 : 12,
          maxRotation: isMobile ? 45 : 0,
          minRotation: isMobile ? 45 : 0,
          font: fontTick(isMobile)
        }
      },
      y: {
        ticks: {
          precision: 0,
          font: fontTick(isMobile)
        }
      }
    };
    if (this.eventiComeLinea) {
      scale['y1'] = {position: 'right', grid: {drawOnChartArea: false}, ticks: {precision: 0, font: fontTick(isMobile)}};
    }
    return scale;
  }
}

/** Le barre contano eventi, non fasi distinte come la card TERMINATI: il tooltip lo dice. */
function etichettaTooltip(contesto: TooltipItem<TipoBarre>): string {
  return contesto.datasetIndex === 2
    ? `Eventi terminati: ${contesto.formattedValue}`
    : `${contesto.dataset.label}: ${contesto.formattedValue}`;
}
